import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { IGridDevice } from 'app/shared/model/grid-device.model';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Translate, translate } from 'react-jhipster';
import { Input, Spinner } from 'reactstrap';
import { getGridDevices, reset } from './grid-devices.reducer';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;
const HOVER_POPUP_WIDTH = 200;

interface GridDevicesPanelProps {
  gridId: number;
}

const isDeviceActive = (state?: string) => (state ?? '').toUpperCase() === 'ONLINE';

export const GridDevicesPanel = ({ gridId }: GridDevicesPanelProps) => {
  const dispatch = useAppDispatch();

  const devices = useAppSelector(state => state.gridDevices.devices);
  const loading = useAppSelector(state => state.gridDevices.loading);
  const errorMessage = useAppSelector(state => state.gridDevices.errorMessage);
  const page = useAppSelector(state => state.gridDevices.page);
  const size = useAppSelector(state => state.gridDevices.size);
  const totalItems = useAppSelector(state => state.gridDevices.totalItems);

  // The backend doesn't always return an x-total-count header; when it's missing, fall back to
  // "a full page came back, so there's probably more" instead of hiding pagination altogether.
  const first = page <= 0;
  const last = totalItems != null ? (page + 1) * size >= totalItems : devices.length < size;
  const totalPages = totalItems != null ? Math.max(1, Math.ceil(totalItems / size)) : null;
  const showPagination = !first || !last;

  const [activePage, setActivePage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hoveredDevice, setHoveredDevice] = useState<IGridDevice | null>(null);
  const [hoverPos, setHoverPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    setActivePage(0);
    setSearchTerm('');
    setDebouncedSearch('');
    return () => {
      dispatch(reset());
    };
  }, [gridId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setActivePage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    dispatch(getGridDevices({ gridId, page: activePage, size: PAGE_SIZE, sort: 'name,asc', search: debouncedSearch || undefined }));
  }, [gridId, activePage, debouncedSearch]);

  const handleDeviceHover = (device: IGridDevice, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredDevice(device);
    setHoverPos({
      top: rect.top,
      left: Math.min(rect.right + 8, window.innerWidth - HOVER_POPUP_WIDTH - 8),
    });
  };

  const handleDeviceLeave = () => {
    setHoveredDevice(null);
    setHoverPos(null);
  };

  return (
    <div className="grid-devices-panel">
      <div className="grid-devices-header">
        <span className="grid-devices-title">
          <Translate contentKey="rainspotApp.gridManagement.devices.title">Devices</Translate>
        </span>
        {totalItems != null && totalItems > 0 && <span className="grid-devices-count-badge">{totalItems}</span>}
      </div>

      <div className="grid-devices-search">
        <FontAwesomeIcon icon="search" className="grid-devices-search-icon" />
        <Input
          bsSize="sm"
          data-cy="gridDevicesSearchInput"
          placeholder={translate('rainspotApp.gridManagement.devices.searchPlaceholder')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className="grid-devices-search-clear"
            aria-label={translate('entity.action.cancel')}
            onClick={() => setSearchTerm('')}
          >
            <FontAwesomeIcon icon="times-circle" />
          </button>
        )}
      </div>

      <div className="grid-devices-list" data-cy="gridDevicesList">
        {loading && (
          <div className="grid-devices-loading">
            <Spinner size="sm" color="primary" />
          </div>
        )}
        {!loading && errorMessage && (
          <div className="grid-devices-empty grid-devices-error">
            <Translate contentKey="rainspotApp.gridManagement.devices.loadError">Failed to load devices</Translate>
          </div>
        )}
        {!loading && !errorMessage && devices.length === 0 && (
          <div className="grid-devices-empty text-muted">
            {debouncedSearch ? (
              <Translate contentKey="rainspotApp.gridManagement.devices.noMatch">No devices match your search</Translate>
            ) : (
              <Translate contentKey="rainspotApp.gridManagement.devices.notFound">No devices found in this grid</Translate>
            )}
          </div>
        )}
        {!loading &&
          !errorMessage &&
          devices.map(device => {
            const active = isDeviceActive(device.state);
            const statusLabel = translate(
              active ? 'rainspotApp.gridManagement.devices.active' : 'rainspotApp.gridManagement.devices.inactive',
            );
            const displayName = device.name || device.serialNumber || `#${device.id}`;
            return (
              <div
                key={device.id}
                className="grid-device-row"
                data-cy="gridDeviceRow"
                onMouseEnter={e => handleDeviceHover(device, e)}
                onMouseLeave={handleDeviceLeave}
              >
                <span
                  className={`grid-device-status-dot ${active ? 'grid-device-status-active' : 'grid-device-status-inactive'}`}
                  title={device.state ?? statusLabel}
                />
                <div className="grid-device-info">
                  <span className="grid-device-name" title={displayName}>
                    {displayName}
                  </span>
                  {device.name && (
                    <span className="grid-device-serial" title={device.serialNumber}>
                      {device.serialNumber}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {showPagination && (
        <div className="grid-devices-pagination">
          <button
            type="button"
            className="grid-icon-btn"
            data-cy="gridDevicesPrevPage"
            disabled={first || loading}
            onClick={() => setActivePage(p => Math.max(0, p - 1))}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="grid-devices-page-label">
            {totalPages != null ? (
              <Translate contentKey="rainspotApp.gridManagement.devices.pageInfo" interpolate={{ page: page + 1, totalPages }}>
                {`Page ${page + 1} of ${totalPages}`}
              </Translate>
            ) : (
              <Translate contentKey="rainspotApp.gridManagement.devices.pageInfoUnknownTotal" interpolate={{ page: page + 1 }}>
                {`Page ${page + 1}`}
              </Translate>
            )}
          </span>
          <button
            type="button"
            className="grid-icon-btn"
            data-cy="gridDevicesNextPage"
            disabled={last || loading}
            onClick={() => setActivePage(p => p + 1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {hoveredDevice &&
        hoverPos &&
        createPortal(
          <div className="grid-device-hover-popup" data-cy="gridDeviceHoverPopup" style={{ top: hoverPos.top, left: hoverPos.left }}>
            <div className="grid-device-hover-row">
              <span className="grid-device-hover-label">
                <Translate contentKey="rainspotApp.gridManagement.devices.status">Status</Translate>
              </span>
              <span className="grid-device-hover-value">
                <span
                  className={`grid-device-status-dot ${
                    isDeviceActive(hoveredDevice.state) ? 'grid-device-status-active' : 'grid-device-status-inactive'
                  }`}
                />
                {hoveredDevice.state ??
                  translate(
                    isDeviceActive(hoveredDevice.state)
                      ? 'rainspotApp.gridManagement.devices.active'
                      : 'rainspotApp.gridManagement.devices.inactive',
                  )}
              </span>
            </div>
            <div className="grid-device-hover-row">
              <span className="grid-device-hover-label">
                <Translate contentKey="rainspotApp.gridManagement.latitude">Latitude</Translate>
              </span>
              <span className="grid-device-hover-value">{hoveredDevice.latitude ?? '—'}</span>
            </div>
            <div className="grid-device-hover-row">
              <span className="grid-device-hover-label">
                <Translate contentKey="rainspotApp.gridManagement.longitude">Longitude</Translate>
              </span>
              <span className="grid-device-hover-value">{hoveredDevice.longitude ?? '—'}</span>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GridDevicesPanel;
